import { observer } from "mobx-react";
import { shouldShowModuleSelector } from "../../_DataEndpointConfig/moduleSelector";
import type ChartCardStore from "../ChartCardStore";
import ClassifierControls from "./ClassifierControls";
import ClassifierGroupControl from "./ClassifierGroupControl";
import ComparePrevAndRenderModeControls from "./ComparePrevAndRenderModeControls";
import { getControlModules } from "./controlModules";
import LLMClassifierControl from "./LLMClassifierControl";
import QuestionControl from "./QuestionControl";
import SkillGroupingControl from "./SkillGroupingControl";
import ModulesControl from "./ModulesControl";
import SingleModuleControl from "./SingleModuleControl";
import ThemesControl from "./ThemesControl";
import TopicsControl from "./TopicsControl";

const ChartControls = observer(({ store }: { store: ChartCardStore }) => {
    // View-only users (non-editable dashboards) still see the chart-level
    // selectors so they know what the chart is reporting on, but each control
    // renders itself disabled off `store.isEditable` so they cannot change the
    // selection. AUT-8882.
    const showClassifierControls = store.selectionType === "Classifiers";
    const showQuestionControls =
        store.selectionType === "Question" ||
        store.selectionType === "Questions" ||
        store.selectionType === "Tags";
    const showModulesControl =
        store.selectionType === "Modules" && !store.config.dataGrid;
    const showTopicControls = store.selectionType === "Topics";
    const showThemeControls = store.selectionType === "Themes";
    const showLLMClassifierControl =
        store.selectionType === "LLM Classifier" ||
        store.selectionType === "LLM Classifier Results";
    const showClassifierGroupControl =
        store.selectionType === "Classifier Group";
    const showComparePrevCheckbox =
        store.config.capabilities.comparePrev && !store.groupingStore.isGrouped;
    const showPercentToggle = store.config.capabilities.renderModes.length > 1;

    const modulesToUse = getControlModules(store);

    // Endpoints can hide the selector when their scoped pool leaves nothing to choose between
    // (e.g. a tenant with a single Quality module). The chart still requests that module.
    const showSingleModuleControl =
        store.selectionType === "Module" &&
        shouldShowModuleSelector(store.config.moduleSelector, modulesToUse);

    return (
        <>
            {showClassifierControls && (
                <ClassifierControls
                    store={store}
                    showComparePrevCheckbox={showComparePrevCheckbox}
                />
            )}
            {showQuestionControls && (
                <QuestionControl
                    store={store}
                    modules={modulesToUse}
                    showComparePrevCheckbox={showComparePrevCheckbox}
                />
            )}
            {showModulesControl && (
                <ModulesControl
                    store={store}
                    modules={modulesToUse}
                    showComparePrevCheckbox={showComparePrevCheckbox}
                />
            )}
            {showTopicControls && <TopicsControl store={store} />}
            {showThemeControls && <ThemesControl store={store} />}
            {showLLMClassifierControl && <LLMClassifierControl store={store} />}
            {showClassifierGroupControl && (
                <ClassifierGroupControl store={store} />
            )}
            {showSingleModuleControl && (
                <SingleModuleControl modules={modulesToUse} store={store} />
            )}
            {store.config.skillGroupingSelector && (
                <SkillGroupingControl
                    store={store}
                    selector={store.config.skillGroupingSelector}
                    followsSelector={showSingleModuleControl}
                />
            )}
            {(showComparePrevCheckbox || showPercentToggle) && (
                <ComparePrevAndRenderModeControls
                    store={store}
                    showComparePrevCheckbox={showComparePrevCheckbox}
                    showPercentToggle={showPercentToggle}
                />
            )}
        </>
    );
});

export default ChartControls;
